import React, { useState, useMemo } from 'react';
import { Table } from './Table';
import { ParameterDiagramModal } from './ParameterDiagramModal';
import type { SAIFileResponse } from '../services/api';

interface Parameter {
  id?: string | number;
  label?: string;
  name?: string;
  icon?: string;
  value?: number;
  validRange?: string;
  min?: number;
  max?: number;
  unit?: string;
  type?: string; // Add type field
}

interface Column {
  key: string;
  header: string;
  render?: (item: any) => React.ReactNode;
  cellClassName?: string;
}

interface PredictionModelResultsProps {
  modelName: string;
  selectedSAIFile?: SAIFileResponse | null;
  parameters?: any[];
  onBack: () => void;
  onVerify: () => void;
  onGetNewPrediction: () => void;
  onSavePredictionResult: () => void;
}

export const PredictionModelResults: React.FC<PredictionModelResultsProps> = ({
  modelName,
  selectedSAIFile,
  parameters: paramResult,
  onBack,
  onVerify,
  onGetNewPrediction,
  onSavePredictionResult,
}) => {
  const [isDiagramModalOpen, setIsDiagramModalOpen] = useState(false);
  const diagramImageUrl = selectedSAIFile?.image ?? null;

  // Get parameters from the selected SAI file
  const parameters = useMemo(() =>
    (paramResult?.map(param => ({
      id: param.id?.toString() || '',
      name: param.label || '',
      icon: param.icon || '',
      value: (param.value ?? param.default ?? 0).toString(),
      validRange: `${param.min} - ${param.max}`,
      unit: param.unit || '',
      type: param.type || '', // Include type for sorting
    })) || []).sort((a, b) => {
      // Sort output parameters first
      const aType = (a.type || '').toLowerCase();
      const bType = (b.type || '').toLowerCase();
      if (aType === 'output' && bType !== 'output') return -1;
      if (bType === 'output' && aType !== 'output') return 1;
      return 0;
    }),
    [paramResult]
  );

  const columns: Column[] = [
    {
      key: 'name',
      header: 'Parameter Name',
      render: (item: Parameter) => (
        <div className="parameter-name-cell">
          {item.icon && <span className="parameter-icon">{item.icon}</span>}
          <span>{item.name}</span>
        </div>
      ),
    },
    {
      key: 'value',
      header: 'Value',
      render: (item: Parameter) => {
        const num = Number(item.value);
        const display = Number.isNaN(num) ? item.value : parseFloat(num.toFixed(5));
        return (
          <span className="value-cell-tooltip-wrap">
            {display}
            <span className="value-cell-tooltip">
              {/* {item.name ?? ''} */}
              {/* <br /> */}
              {item.value}
            </span>
          </span>
        );
      },
    },
    {
      key: 'validRange',
      header: 'Valid Range',
      render: (item: Parameter) => <span>{item.validRange}</span>,
    },
    {
      key: 'unit',
      header: 'Unit',
      render: (item: Parameter) => <span>{item.unit}</span>,
    },
  ];

  return (
    <div className="wizard-step-container">
      {/* Header */}
      <div className="wizard-step-header">
        <button className="back-button" onClick={onBack}>
          <span className="back-arrow">←</span> Back
        </button>
        <h1>Prediction Model Results</h1>
        <div className="header-actions">
          <button className="btn btn-secondary view-button" onClick={onVerify}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 18 17" fill="none">
              <path d="M5.83301 7.49992L8.33301 9.99992L16.6663 1.66659M11.6663 0.833252H4.83301C3.43288 0.833252 2.73281 0.833252 2.19803 1.10574C1.72763 1.34542 1.34517 1.72787 1.10549 2.19828C0.833008 2.73305 0.833008 3.43312 0.833008 4.83325V11.8333C0.833008 13.2334 0.833008 13.9334 1.10549 14.4682C1.34517 14.9386 1.72763 15.3211 2.19803 15.5608C2.73281 15.8333 3.43288 15.8333 4.83301 15.8333H11.833C13.2331 15.8333 13.9332 15.8333 14.468 15.5608C14.9384 15.3211 15.3208 14.9386 15.5605 14.4682C15.833 13.9334 15.833 13.2334 15.833 11.8333V8.33325" stroke="#3C4858" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Verify
          </button>
          <button className="btn btn-secondary view-button" onClick={onGetNewPrediction}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M14.167 4.27108C15.9348 5.55903 17.0837 7.6453 17.0837 9.99992C17.0837 13.9119 13.9123 17.0833 10.0003 17.0833H9.58366M5.83366 15.7288C4.06588 14.4408 2.91699 12.3545 2.91699 9.99992C2.91699 6.0879 6.08831 2.91659 10.0003 2.91659H10.417M10.8337 18.6666L9.16699 16.9999L10.8337 15.3333M9.16699 4.66659L10.8337 2.99992L9.16699 1.33325" stroke="#3C4858" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Get New Prediction
          </button>
          <button className="btn btn-secondary view-button" onClick={() => setIsDiagramModalOpen(true)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="15" viewBox="0 0 21 15" fill="none">
              <path d="M13.2163 7.17921C13.2163 8.88556 11.833 10.2688 10.1267 10.2688C8.42032 10.2688 7.03706 8.88556 7.03706 7.17921C7.03706 5.47285 8.42032 4.08958 10.1267 4.08958C11.833 4.08958 13.2163 5.47288 13.2163 7.17921ZM19.1909 7.17924C19.1909 7.17924 16.981 1 10.1267 1C3.27239 1 1.0625 7.17924 1.0625 7.17924C1.0625 7.17924 3.27239 13.3585 10.1267 13.3585C16.981 13.3585 19.1909 7.17924 19.1909 7.17924Z" stroke="#3C4858" stroke-width="2" stroke-miterlimit="10"/>
            </svg>
            View Parameter Diagram
          </button>
          <button className="btn btn-primary" onClick={onSavePredictionResult}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M5.83333 2.5V5.33333C5.83333 5.80004 5.83333 6.0334 5.92416 6.21166C6.00406 6.36846 6.13154 6.49594 6.28834 6.57584C6.4666 6.66667 6.69996 6.66667 7.16667 6.66667H12.8333C13.3 6.66667 13.5334 6.66667 13.7117 6.57584C13.8685 6.49594 13.9959 6.36846 14.0758 6.21166C14.1667 6.0334 14.1667 5.80004 14.1667 5.33333V3.33333M14.1667 17.5V12.1667C14.1667 11.7 14.1667 11.4666 14.0758 11.2883C13.9959 11.1315 13.8685 11.0041 13.7117 10.9242C13.5334 10.8333 13.3 10.8333 12.8333 10.8333H7.16667C6.69996 10.8333 6.4666 10.8333 6.28834 10.9242C6.13154 11.0041 6.00406 11.1315 5.92416 11.2883C5.83333 11.4666 5.83333 11.7 5.83333 12.1667V17.5M17.5 7.77124V13.5C17.5 14.9001 17.5 15.6002 17.2275 16.135C16.9878 16.6054 16.6054 16.9878 16.135 17.2275C15.6002 17.5 14.9001 17.5 13.5 17.5H6.5C5.09987 17.5 4.3998 17.5 3.86502 17.2275C3.39462 16.9878 3.01217 16.6054 2.77248 16.135C2.5 15.6002 2.5 14.9001 2.5 13.5V6.5C2.5 5.09987 2.5 4.3998 2.77248 3.86502C3.01217 3.39462 3.39462 3.01217 3.86502 2.77248C4.3998 2.5 5.09987 2.5 6.5 2.5H12.2288C12.6364 2.5 12.8402 2.5 13.0321 2.54605C13.2021 2.58688 13.3647 2.65422 13.5138 2.7456C13.682 2.84867 13.8261 2.9928 14.1144 3.28105L16.719 5.88562C17.0072 6.17387 17.1513 6.318 17.2544 6.48619C17.3458 6.63531 17.4131 6.79789 17.4539 6.96795C17.5 7.15976 17.5 7.36358 17.5 7.77124Z" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Save Prediction Result
          </button>
        </div>
      </div>

      {/* Description */}
      <p className="prediction-results-description">
        Based on your input parameters, the AI model predicts the performance metrics shown below.
        Use these results to validate your simulation setup or adjust parameters.
      </p>

      {/* Tip */}
      <div className="prediction-results-tip">
        <span className="tip-icon">💡</span>
        <span className="tip-text">
          <strong>Tip:</strong> Hover over values to see full parameter names and precision.
        </span>
      </div>

      {/* Results Table */}
      <div className="prediction-results-table-container">
        <Table
          items={parameters}
          columns={columns}
          scrollable={true}
          maxItemsPerPage={10}
          rowClassName={(item) => {
            const itemType = (item.type || '').toLowerCase();
            return itemType === 'output' ? 'entrainment-ratio-row' : '';
          }}
        />
      </div>

      <ParameterDiagramModal
        isOpen={isDiagramModalOpen}
        onClose={() => setIsDiagramModalOpen(false)}
        imageUrl={diagramImageUrl}
      />
    </div>
  );
};

